/**
 * SERVICES DATA — 20 luxury wedding invitation services.
 * Powers /services/:slug pages. Each entry is unique, EEAT-friendly,
 * keyword-rich and 1000-1500 words when rendered (paragraphs + bullets + FAQs).
 *
 * Schema fields:
 *  - slug: URL slug (e.g. "luxury-wedding-invitations")
 *  - title: page H1
 *  - metaTitle, metaDescription
 *  - eyebrow, intro: hero block
 *  - sections: [{h2, paragraphs[], bullets[]}]
 *  - faqs: [{q, a}]
 *  - keywords: primary + secondary
 *  - heroImage
 *  - relatedSlugs: other service slugs to link
 */

const HERO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/wssipa5q_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg";
const HERO_MUMBAI =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/fnvfaxzf_IMG_3305.JPG.jpeg";
const HERO_TEMPLE =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/buyzfj3n_WhatsApp%20Image%202026-04-24%20at%2022.07.12.jpeg";
const HERO_NEWS =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/w40hkg7j_WhatsApp%20Image%202026-03-04%20at%2020.03.19%20-%20Copy.jpeg";
const HERO_WELCOME =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/5zfsxqqn_WhatsApp%20Image%202026-02-14%20at%2023.56.46%20-%20Copy.jpeg";
const HERO_TAGS =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/h5dd1tf7_WhatsApp%20Image%202026-04-18%20at%2014.09.09.jpeg";
const HERO_NISH =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/mnpfelf3_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg";

export const SERVICES = [
  {
    slug: "luxury-wedding-invitations",
    title: "Luxury Wedding Invitations",
    eyebrow: "The Atelier · Signature",
    metaTitle:
      "Luxury Wedding Invitations | Bespoke Designs by InvitationWala",
    metaDescription:
      "Commission a luxury wedding invitation crafted around your story — bespoke typography, cinematic motion, gold-foil details. Delivered on WhatsApp in 24 hours.",
    keywords: [
      "luxury wedding invitations",
      "premium wedding invitations India",
      "bespoke wedding invitation design",
      "luxury wedding invite designer",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "Luxury is in the details no one else can copy. At InvitationWala we design wedding invitations the way master jewellers design heirlooms — one couple, one story, one quietly extraordinary piece. Every typeface is set by hand, every motion frame storyboarded, every shade chosen to live beside your real wedding palette. The finished invite is delivered to your WhatsApp as a cinematic film, an animated card and a still keepsake — ready for the family group, the stories, the broadcast list and the printer.",
    sections: [
      {
        h2: "What makes a wedding invitation truly luxurious",
        paragraphs: [
          "True luxury is never loud. It is the breath between two words, the weight of a serif at the right size, the patience of a designer who refuses to ship a frame they do not love. We approach every brief like a portrait — listening first, sketching second, animating last. The result is an invite that feels like the family it belongs to.",
          "We hand-build every project — no templates, no marketplace presets, no AI shortcuts in the creative direction. Our typography is set in heritage faces (Cormorant, Sangam, Optima, custom monograms), our motion is choreographed to your chosen score, and our colour palettes are sampled from your real wedding outfits, venue and family heirlooms.",
        ],
        bullets: [
          "Hand-set heritage typography — no Canva, no marketplace templates",
          "Custom illustrated city skyline, deity, venue or monogram on every cover",
          "Gold-foil emulation in motion + companion print-ready stationery files",
          "Cinematic 30-90 second invite film with original score sync",
          "Three concept routes presented before a single frame is animated",
        ],
      },
      {
        h2: "Our luxury wedding invitation process",
        paragraphs: [
          "We begin with a quiet conversation — usually on WhatsApp, sometimes on a Google Meet. We listen for the family story, the function timeline, the references that have lingered in your saved folder. Within 48 hours you receive three concept mood-boards: distinct directions, each with palette, typography pair, motion language and reference frames.",
          "Once a direction is chosen, our motion team builds the master invite — typography first, illustration second, music third. You see the invite come alive slide by slide so nothing is a surprise. Approvals happen frame by frame on the same WhatsApp thread, in your own words and language.",
        ],
        bullets: [
          "Day 0: WhatsApp brief + reference exchange",
          "Day 1-2: Three concept mood-boards delivered",
          "Day 3-4: Master invite film built slide-by-slide",
          "Day 5: Function cards, RSVP microsite + printed stationery files",
          "Same-day expedite available for urgent dates",
        ],
      },
      {
        h2: "Who our luxury invites are for",
        paragraphs: [
          "We work with discerning families across India and the diaspora — Indian cricketers, lifestyle creators, founders, doctors, lawyers, hoteliers, and many beautifully private families who simply want their wedding to be remembered. If you have spent months on your venue, your lehenga, your jeweller and your photographer, your invitation deserves the same standard of care. We take that responsibility seriously.",
          "Our most loved clients are couples planning destination weddings in Udaipur, Jaipur, Goa, Mumbai, Lake Como, Tuscany and Phuket; NRI families in London, Dubai, New York, New Jersey, Toronto and Sydney; and second-generation Indian-origin couples who want a design language that feels both heritage-rooted and globally elegant.",
        ],
      },
      {
        h2: "Pricing and what is included",
        paragraphs: [
          "Luxury bespoke invitation suites start from a modest base and scale based on the number of functions, the level of original illustration, and the complexity of the motion. A typical multi-function suite for a four-day Indian wedding (Save the Date, Wedding Day Film, Mehndi + Sangeet + Haldi + Cocktail + Reception cards, RSVP microsite, Welcome screen) is a single, transparent quote — shared on WhatsApp within an hour of your brief.",
          "Every quote includes unlimited rounds of feedback (within reason), a final delivery in MP4, MOV and JPG/PNG formats sized for WhatsApp, Instagram, Stories, broadcast, and a print-ready vector pack for any physical add-ons. Express same-day delivery is available with a small expedite fee.",
        ],
      },
    ],
    faqs: [
      {
        q: "What makes a wedding invitation 'luxury'?",
        a: "A luxury wedding invitation is fully bespoke — hand-set typography, original illustration or monogram, custom motion choreographed to original score, and a delivery format curated for WhatsApp, Instagram and print. There are no templates, no marketplace shortcuts. Every frame is built around your family's story, palette and function timeline.",
      },
      {
        q: "How long does a luxury wedding invitation take?",
        a: "A signature InvitationWala suite is typically ready in 5-7 days from the time we receive your brief and assets. Express same-day delivery is available for urgent dates with a small expedite fee. Most cinematic invite films are ready within 24-48 hours.",
      },
      {
        q: "Do you ship printed luxury wedding stationery?",
        a: "Yes — our sister brand Stationery Wala produces letterpressed, foiled and laser-cut physical stationery to match every digital invite. Welcome signs, itinerary cards, escort cards, menus, monogram favours and luggage tags ship across India and internationally for destination and NRI weddings.",
      },
      {
        q: "Can I see a sample before booking?",
        a: "Absolutely. Once we understand your brief, we share a curated reel of past luxury weddings closest to your aesthetic. We never share another client's full delivery — discretion is part of the service — but we will walk you through reference work, palettes and motion samples.",
      },
      {
        q: "Do you work with NRI couples outside India?",
        a: "Yes. About 35% of our luxury clients are NRI families across UAE, UK, USA, Canada, Australia and Singapore. The entire process happens on WhatsApp — we have delivered invites at 3 AM IST to match couples on EST and PST schedules.",
      },
      {
        q: "What if my family doesn't like the first draft?",
        a: "That is exactly why we share three distinct concept routes before animating a single frame, and review the master invite slide by slide. Unlimited rounds of refinement are included until the family signs off. We have never had a project that wasn't loved by the end.",
      },
    ],
    relatedSlugs: [
      "premium-wedding-invitations",
      "personalized-wedding-invitations",
      "royal-wedding-invitations",
      "destination-wedding-invitations",
      "wedding-monogram-design",
    ],
  },
  {
    slug: "premium-wedding-invitations",
    title: "Premium Wedding Invitations",
    eyebrow: "Signature · Studio",
    metaTitle:
      "Premium Wedding Invitations Online | Custom Designs | InvitationWala",
    metaDescription:
      "Premium wedding invitations designed end-to-end with motion, illustration and original typography. WhatsApp delivery in 24 hours. Custom quotes within an hour.",
    keywords: [
      "premium wedding invitations",
      "premium wedding invite design",
      "premium wedding invitation cards",
      "high-end wedding invites",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "Premium wedding invitations sit at the intersection of craft and clarity. They are unmistakably elegant — but never overwhelming. At InvitationWala we design premium invites for couples who want the finished piece to feel quietly expensive, exquisitely on-brand and effortlessly shareable. Every premium invite is built around a single visual concept, dressed with subtle motion, paired with a complementary score, and delivered in WhatsApp-ready formats within 24 hours of approval.",
    sections: [
      {
        h2: "The premium difference, in design",
        paragraphs: [
          "A premium invite makes one strong choice and commits to it. A single illustration, a single typeface pair, a single palette of three colours. That restraint is what makes it premium. We help couples find that single hook — a city silhouette, a deity, a monogram, a piece of heritage jewellery — and design the entire suite outwards from it.",
          "Where luxury is fully bespoke from a blank page, premium is curated bespoke — we draw on a refined library of motion templates and illustration styles, then customise everything to your story. The result feels personal, premium and arrives faster than a fully ground-up build.",
        ],
        bullets: [
          "One signature illustration or monogram, applied across the full suite",
          "Animated save-the-date, wedding film and function cards",
          "Premium serif + script type pairing chosen for your brief",
          "MP4 + GIF + still formats sized for WhatsApp & Instagram",
          "RSVP-ready microsite link with guest tracking",
        ],
      },
      {
        h2: "What's included in a premium wedding invitation suite",
        paragraphs: [
          "Our standard premium suite covers an animated save-the-date, the main wedding day invite film, individual cards for every function (Mehndi, Haldi, Sangeet, Cocktail, Reception), an RSVP microsite, and a print-ready PDF for couples who want a physical version. Every asset shares one cohesive visual language — so when guests forward the invite to their family group, every detail rhymes.",
        ],
        bullets: [
          "Save-the-Date — 10-15 sec reel for Instagram and WhatsApp status",
          "Wedding Day Film — 30-60 sec cinematic invite",
          "Function Cards — Mehndi, Haldi, Sangeet, Cocktail, Reception",
          "RSVP Microsite — branded link with guest count & meal preference",
          "Printable PDF — A5 or DL, vectors included",
        ],
      },
      {
        h2: "Why premium beats template invites",
        paragraphs: [
          "Template invites are everywhere. Your aunt has seen them. Your cousin made one in Canva last weekend. Premium invites stand apart because they tell a recognisable story — the moment a guest opens it, they know exactly which family is getting married. That micro-moment of recognition is worth the upgrade.",
          "Premium invites also age better. A template invite is dated the day it's sent. A premium invite, anchored in a custom illustration and a considered typeface, becomes the keepsake your families re-watch on anniversaries, send to grandchildren, and frame on the wall.",
        ],
      },
    ],
    faqs: [
      {
        q: "How is a premium invitation different from a luxury one?",
        a: "Premium uses a refined library of motion + illustration styles that we customise to your story — faster turnaround, exquisite quality. Luxury starts from a blank page with original illustration and full bespoke direction. Both are templated-free; premium is curated bespoke, luxury is fully bespoke.",
      },
      {
        q: "What's the price range for a premium wedding invite?",
        a: "Premium suites are mid-tier in our pricing — accessible for couples who want a fully designed multi-function suite without commissioning original illustration. Share your function list on WhatsApp and we send a transparent quote within an hour.",
      },
      {
        q: "How fast can I get a premium invite?",
        a: "Standard turnaround is 24-48 hours from final brief. Same-day delivery is possible for save-the-dates and single-function invites. Full multi-function suites typically take 3-4 days.",
      },
      {
        q: "Can I see edits before approval?",
        a: "Yes — every premium suite is built slide by slide with you on WhatsApp. You sign off each function card before the next begins. Unlimited refinement rounds are included for typography, colour and animation timing.",
      },
      {
        q: "Do I get all the files for printing too?",
        a: "Every premium suite comes with print-ready PDF and PNG files. If you want letterpressed or foiled physical stationery, our sister brand Stationery Wala will produce it for you.",
      },
      {
        q: "Will my premium invite be unique?",
        a: "Yes. Every illustration, monogram and palette is customised to your family — no two premium suites share the exact same combination. We also never re-use a custom illustration for another couple.",
      },
    ],
    relatedSlugs: [
      "luxury-wedding-invitations",
      "wedding-invitation-cards",
      "digital-wedding-invitations",
      "personalized-wedding-invitations",
      "save-the-date-cards",
    ],
  },
  {
    slug: "wedding-invitation-cards",
    title: "Wedding Invitation Cards",
    eyebrow: "Classic · Modern",
    metaTitle:
      "Wedding Invitation Cards Online | Custom Card Design | InvitationWala",
    metaDescription:
      "Custom wedding invitation cards designed and delivered digitally on WhatsApp — also printable. Hindu, Muslim, Sikh, Christian and inter-faith formats. 24 hr delivery.",
    keywords: [
      "wedding invitation cards",
      "wedding card design",
      "wedding card online",
      "custom wedding cards",
    ],
    heroImage: HERO_NEWS,
    intro:
      "A wedding invitation card is the first impression your wedding makes on every guest. At InvitationWala we design custom wedding invitation cards across every faith, function and aesthetic — from classic Hindu ceremonial cards to minimalist destination invites, from Muslim wedding invitations with calligraphy to Sikh anand karaj cards, Christian church invites and elegant inter-faith pieces. Each card is delivered digitally for WhatsApp and Instagram, and is print-ready for physical handouts.",
    sections: [
      {
        h2: "Wedding card formats we design",
        paragraphs: [
          "We design wedding cards for every Indian wedding tradition — and for cross-cultural couples who want their card to reflect both families. Whether your card needs Devanagari script, Urdu calligraphy, Gurmukhi, Tamil, Telugu, Bengali, Marathi or Malayalam — we set it correctly, with the right typography conventions and the right religious symbols (Ganesh, Bismillah, Ek Onkar, Cross, etc.).",
        ],
        bullets: [
          "Hindu wedding cards — Ganesh, mandap, kalash, swastika motifs",
          "Muslim wedding cards — Bismillah, geometric, Urdu calligraphy",
          "Sikh wedding cards — Ek Onkar, Anand Karaj design language",
          "Christian wedding cards — cross, dove, classical typography",
          "Inter-faith cards — modern, secular, monogram-led design",
        ],
      },
      {
        h2: "Digital + Print — both included",
        paragraphs: [
          "Every wedding invitation card we design comes with a digital format (for WhatsApp, email, Instagram broadcast) and a print-ready vector format (for couples who want physical cards too). Our sister brand Stationery Wala will print and ship the physical cards if you'd like — letterpressed, foiled, or laser-cut on premium uncoated paper.",
        ],
      },
      {
        h2: "Easy WhatsApp delivery",
        paragraphs: [
          "Once your wedding invitation card is approved, you receive it as an MP4 video (animated), a JPG/PNG still and a print-ready PDF. The MP4 is sized for WhatsApp's 16MB limit, the JPG works for Instagram broadcast, and the PDF is ready for any printer. Forward it to the family group, post it as your status, send it to your guest list — done.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I get a wedding invitation card in regional languages?",
        a: "Yes — we typeset wedding cards in Hindi, Urdu, Punjabi, Gujarati, Marathi, Tamil, Telugu, Bengali, Malayalam and Kannada. We use the correct script conventions and traditional typography for each language.",
      },
      {
        q: "Do you do bilingual wedding invitation cards?",
        a: "Absolutely — bilingual cards (English + a regional language, or two regional languages) are one of our most requested formats, especially for inter-community and NRI weddings.",
      },
      {
        q: "Can the wedding card be printed too?",
        a: "Yes. Every digital wedding card we design comes with a print-ready vector PDF. Our sister brand Stationery Wala offers letterpressed, foiled and laser-cut physical printing across India and internationally.",
      },
      {
        q: "How much does a custom wedding invitation card cost?",
        a: "Pricing depends on whether you want a still card, an animated card, or a full multi-function suite. Share your requirements on WhatsApp and we send a transparent quote within the hour.",
      },
      {
        q: "Can I edit my card if I find a mistake?",
        a: "Yes — we offer unlimited refinement rounds during the design phase. After final delivery, edits are quick (within hours) for minor changes and chargeable for major design revisions.",
      },
      {
        q: "Will my wedding card work on iPhone and Android?",
        a: "Yes. We deliver in formats (MP4 H.264, JPG, PNG, PDF) that play natively on every smartphone, every WhatsApp version, every Gmail client and every Instagram inbox.",
      },
    ],
    relatedSlugs: [
      "digital-wedding-invitations",
      "online-wedding-invitations",
      "wedding-stationery",
      "save-the-date-cards",
      "personalized-wedding-invitations",
    ],
  },
  {
    slug: "digital-wedding-invitations",
    title: "Digital Wedding Invitations",
    eyebrow: "Always WhatsApp-Ready",
    metaTitle:
      "Digital Wedding Invitations | E-Invites on WhatsApp | InvitationWala",
    metaDescription:
      "Create stunning digital wedding invitations delivered on WhatsApp. Custom designed, ready in 24 hours, eco-friendly, sharable worldwide. Order now.",
    keywords: [
      "digital wedding invitations",
      "digital wedding invite",
      "e-invite wedding",
      "whatsapp wedding invitation",
    ],
    heroImage: HERO_MUMBAI,
    intro:
      "Digital wedding invitations are the modern standard for couples who want their invite to be beautiful, sharable, eco-friendly and instantly delivered. At InvitationWala we design custom digital wedding invitations for couples across India and the diaspora — from animated save-the-dates to cinematic invite films, from minimalist e-cards to fully interactive RSVP microsites. Every digital invite is sized for WhatsApp, optimised for Instagram, and delivered to you within 24 hours.",
    sections: [
      {
        h2: "Why digital wedding invitations win",
        paragraphs: [
          "Digital wedding invitations have overtaken printed cards for one simple reason — they are far more shareable. A guest receives your invite on WhatsApp, watches the film in eight seconds, forwards it to their family, posts it to their stories, and saves the date to their calendar. None of that happens with a printed card sitting in a postbox.",
          "Digital invites are also sustainable, faster to produce, easier to update if a function date changes, and significantly more affordable than printed ceremonial cards. For most couples, digital is the primary invite, with a small batch of printed cards reserved for grandparents and close family.",
        ],
        bullets: [
          "Instant WhatsApp & Instagram delivery — no postage, no delay",
          "Eco-friendly — no paper, no shipping",
          "Update-friendly — change a date or venue without reprinting",
          "Trackable RSVPs via optional microsite",
          "Shareable across WhatsApp, Instagram, Email and SMS",
        ],
      },
      {
        h2: "Formats we deliver",
        paragraphs: [
          "Every digital wedding invitation we design is delivered in three primary formats: an MP4 video for the cinematic experience, a JPG/PNG still for broadcast and forwarding, and an optional RSVP microsite link for guest tracking. All formats are sized for WhatsApp's compression, Instagram's algorithms and email-friendly file weights.",
        ],
        bullets: [
          "Animated MP4 — 9:16 vertical for stories, 1:1 for feed, 16:9 for screens",
          "Still JPG/PNG — high-resolution for forwarding and printing",
          "RSVP microsite — branded link with guest tracking and meal preference",
          "Print-ready PDF — for couples who want a physical version too",
          "GIF format — for guests on older WhatsApp versions",
        ],
      },
      {
        h2: "Custom design, no templates",
        paragraphs: [
          "Every digital wedding invitation we make is custom designed for your story — there are no templates, no marketplace presets, no AI shortcuts. Your invite includes your real photographs, your venue illustration, your typography choice and your selected music. The result is unmistakably yours, not a generic 'wedding template' your guests have seen ten times this season.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is a digital wedding invitation?",
        a: "A digital wedding invitation is a custom-designed video, image or interactive card sent via WhatsApp, Instagram or email instead of a printed card. It includes your wedding details, photographs and design language — and can be forwarded, posted to stories, and saved to phones globally.",
      },
      {
        q: "How fast can I get a digital wedding invitation?",
        a: "Most digital wedding invitations are ready within 24 hours from final brief. Express same-day delivery is available for urgent dates with a small expedite fee.",
      },
      {
        q: "Are digital invitations cheaper than printed cards?",
        a: "Yes — digital wedding invitations are significantly more affordable than premium printed cards because there is no paper, printing or postage cost. A premium digital invite costs less than a single printed Indian ceremonial card from many top stationers.",
      },
      {
        q: "Will guests respect a digital invite?",
        a: "Absolutely. Digital invitations are now the global standard for weddings of every budget, including ultra-luxury celebrity weddings. The quality of the design matters far more than the medium — and a premium digital invite outshines a generic printed one every time.",
      },
      {
        q: "Can older relatives view a digital invite?",
        a: "Yes — we deliver in formats (MP4 H.264, JPG, GIF) that work on every iPhone, Android phone and WhatsApp version. We also offer a printable PDF for grandparents who'd like a physical copy.",
      },
      {
        q: "Can I update the invite if a date changes?",
        a: "Yes — that's one of the biggest advantages of digital. We can update the venue, date, dress code or function timing for a small revision fee — and you simply re-share the new version.",
      },
    ],
    relatedSlugs: [
      "online-wedding-invitations",
      "wedding-invitation-cards",
      "save-the-date-cards",
      "wedding-website",
      "personalized-wedding-invitations",
    ],
  },
  {
    slug: "online-wedding-invitations",
    title: "Online Wedding Invitations",
    eyebrow: "Built for the Internet",
    metaTitle:
      "Online Wedding Invitations | Create & Send E-Invites | InvitationWala",
    metaDescription:
      "Create beautiful online wedding invitations and send them instantly via WhatsApp, Instagram or email. Custom designs, RSVP tracking, worldwide delivery.",
    keywords: [
      "online wedding invitations",
      "online wedding invite",
      "create online wedding invitation",
      "wedding invite online",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "Online wedding invitations are designed to be shared, forwarded and discovered — the way modern weddings actually unfold. At InvitationWala we craft online wedding invitations that travel beautifully across every channel a guest will see — WhatsApp, Instagram stories, email broadcast, family groups, and even cross-platform shares to NRI relatives abroad.",
    sections: [
      {
        h2: "What 'online' really means for your wedding invite",
        paragraphs: [
          "An online wedding invitation isn't just a digital version of a printed card — it is designed from the ground up for screens. That means vertical aspect ratios for stories, square ratios for feed posts, MP4 compression sized for WhatsApp, and a hook strong enough to stop the scroll. We design every online invite with the platform behaviour in mind.",
        ],
        bullets: [
          "9:16 vertical version for WhatsApp status & Instagram stories",
          "1:1 square version for Instagram feed posts",
          "16:9 horizontal version for desktop & screen displays",
          "MP4 compressed to under 16MB for WhatsApp limits",
          "GIF version for inline email previews",
        ],
      },
      {
        h2: "Send it everywhere, instantly",
        paragraphs: [
          "Once your online wedding invitation is ready, you can send it to your entire guest list in under five minutes — via WhatsApp broadcast, Instagram DM, email, SMS, or simply by posting it as your status. No printing, no envelope-stuffing, no postage delays. Your overseas family receives the same invite at the same time as your next-door neighbour.",
        ],
      },
      {
        h2: "Trackable RSVP, optionally",
        paragraphs: [
          "Every online wedding invitation can be paired with a branded RSVP microsite — a one-page wedding website where guests confirm attendance, choose meal preference, and add the wedding to their calendar. You get a live admin dashboard with the guest list and counts, downloadable for the catering team.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I send online wedding invitations to my guest list?",
        a: "Once we deliver your online wedding invitation, you can share it via WhatsApp broadcast (up to 256 contacts per list), Instagram DM, email broadcast, SMS, or simply by posting to your stories. Most couples use a WhatsApp broadcast as the primary channel and Instagram stories as the secondary.",
      },
      {
        q: "Can guests RSVP to an online wedding invitation?",
        a: "Yes — we include an optional RSVP microsite with every online invite. Guests click a link, confirm attendance, choose meal preference, and add the wedding to their phone calendar.",
      },
      {
        q: "Will my online invite look good on every phone?",
        a: "Yes. We deliver in universal formats (MP4 H.264, JPG, PNG, GIF) sized for WhatsApp's compression, Instagram's algorithms and email-safe weights. They render identically on iPhone, Android, and every WhatsApp version.",
      },
      {
        q: "Can I have an online invite in multiple languages?",
        a: "Absolutely. We design bilingual and multilingual online wedding invitations in Hindi, Urdu, Punjabi, Gujarati, Marathi, Tamil, Telugu, Bengali, Malayalam, Kannada and English.",
      },
      {
        q: "How fast can I get an online wedding invitation?",
        a: "Most online wedding invitations are delivered within 24 hours of final brief. Express same-day delivery is available for urgent dates.",
      },
      {
        q: "Are online wedding invitations safe to forward?",
        a: "Yes — your invite contains no personal data beyond what you choose to share publicly. Many couples keep venue address private and only share it via the RSVP microsite link to confirmed guests.",
      },
    ],
    relatedSlugs: [
      "digital-wedding-invitations",
      "wedding-invitation-cards",
      "wedding-website",
      "wedding-rsvp-website",
      "save-the-date-cards",
    ],
  },
  {
    slug: "wedding-website",
    title: "Wedding Website",
    eyebrow: "Your Wedding's Home Online",
    metaTitle:
      "Wedding Website Design | Custom Wedding Sites | InvitationWala",
    metaDescription:
      "Beautiful, custom wedding websites with RSVP, story, venue, travel and gift registry pages. Mobile-first, fast and built around your wedding's design language.",
    keywords: [
      "wedding website",
      "wedding website design",
      "custom wedding website",
      "wedding microsite",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "A wedding website is the central hub for your wedding — one elegant URL where guests find every detail they'll need across every function, every location and every time zone. At InvitationWala we design and build custom wedding websites that match the design language of your invitation, run beautifully on every phone, and include RSVP, travel, story, photo gallery and registry sections — all without a single template.",
    sections: [
      {
        h2: "What goes on a wedding website",
        paragraphs: [
          "Every wedding website we build is structured around the guest journey — from first link click to wedding day. The homepage tells your story in three scrolls, the schedule page covers every function across every day, the venue page maps every location with travel tips, the RSVP page collects guest details, and the gallery captures the build-up moments leading to the wedding.",
        ],
        bullets: [
          "Story page — how you met, in your words",
          "Schedule — every function across every day",
          "Venue & travel — maps, hotel suggestions, transport",
          "RSVP — guest confirmation with meal preference",
          "Gallery — pre-wedding shoot & memories",
          "Registry — optional gift list",
          "FAQ — dress code, kids, plus-ones, COVID protocol",
        ],
      },
      {
        h2: "Designed to match your invitation",
        paragraphs: [
          "Your wedding website shares the same typography, palette, motifs and tone as your wedding invitation suite. Guests who receive your invite and click through to the website feel one continuous brand experience — not two separate designs glued together. This consistency is what separates a custom wedding website from a generic template builder.",
        ],
      },
      {
        h2: "Mobile-first, fast, and global",
        paragraphs: [
          "Every wedding website we build is mobile-first — because that's how 92% of your guests will view it. Pages load in under two seconds even on average 4G, images are lazy-loaded, animations are reduced for low-power devices, and the entire site works offline-first for guests with patchy connectivity. We host on global CDNs so a guest in London opens it as fast as a guest in Lucknow.",
        ],
      },
    ],
    faqs: [
      {
        q: "Why do we need a wedding website?",
        a: "A wedding website gives every guest a single, beautiful place to find all your wedding details — schedule, venues, travel, RSVP, dress code, and story — without you having to answer the same question fifty times on WhatsApp. It also lets NRI relatives feel included in the build-up.",
      },
      {
        q: "How long does it take to build a wedding website?",
        a: "Standard wedding websites are ready in 5-7 days from final brief. Express delivery (3 days) is available for urgent dates. The RSVP page goes live first if you need to collect responses immediately.",
      },
      {
        q: "Do you handle the domain and hosting?",
        a: "Yes — we register a custom domain (yournames.com), set up hosting on a global CDN, configure SSL for security, and hand over a simple admin dashboard you can edit yourself if needed.",
      },
      {
        q: "Can guests RSVP through the website?",
        a: "Yes — every wedding website includes a full RSVP system with meal preference, plus-one tracking, function-by-function attendance, and an admin dashboard showing live counts for your catering team.",
      },
      {
        q: "Will my wedding website work in multiple languages?",
        a: "Yes — we build multilingual wedding websites with English + a regional language, or with full language switching for international guest lists.",
      },
      {
        q: "How much does a custom wedding website cost?",
        a: "A custom wedding website typically falls in the mid-tier of our pricing — affordable for any couple investing in a premium digital invitation suite. Share your requirements on WhatsApp for a transparent quote.",
      },
    ],
    relatedSlugs: [
      "wedding-website-builder",
      "wedding-rsvp-website",
      "online-wedding-invitations",
      "digital-wedding-invitations",
      "destination-wedding-invitations",
    ],
  },
  {
    slug: "wedding-website-builder",
    title: "Wedding Website Builder",
    eyebrow: "Done-for-You Builder",
    metaTitle:
      "Wedding Website Builder | Custom Builder Service | InvitationWala",
    metaDescription:
      "Skip the DIY template builders. Our wedding website builder service crafts a fully custom wedding site for you in days — RSVP, schedule, gallery and more.",
    keywords: [
      "wedding website builder",
      "wedding site builder",
      "best wedding website builder",
      "wedding website maker",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "Most wedding website builders ask you to spend hours picking templates, dragging blocks, fighting with their editor — and the end result still looks like every other template wedding. InvitationWala's wedding website builder service is the opposite: we do the building for you. You send the photos and the function list, we deliver a fully custom wedding website matched to your invitation aesthetic. No drag-and-drop. No template DNA. No weekend spent learning a CMS.",
    sections: [
      {
        h2: "Builder service vs. DIY template builders",
        paragraphs: [
          "DIY wedding website builders (the popular ones you've probably seen advertised) are perfectly fine for simple weddings — but they constrain you to their templates, their fonts, their layouts and their watermarks. Our builder service starts from a blank page, designs to match your invite, and gives you a unique URL with no third-party branding.",
        ],
        bullets: [
          "Done-for-you — we build the entire site, you only review",
          "Fully custom — typography, palette and layout match your invite",
          "No template DNA — your site looks unmistakably yours",
          "Premium domain & SSL included",
          "Lightweight CMS for couple-side edits after launch",
        ],
      },
      {
        h2: "Our build process",
        paragraphs: [
          "We begin with a single 30-minute WhatsApp brief where you walk us through your wedding — venues, functions, story, photographs. Within 48 hours we deliver a design mockup of every page (Home, Story, Schedule, Venue, RSVP, Gallery, Registry, FAQ). After approval we build, integrate the RSVP system, register the domain, set up hosting, and hand you a live URL within 5-7 days.",
        ],
      },
      {
        h2: "Post-launch support",
        paragraphs: [
          "Every wedding website we build comes with 90 days of post-launch support — minor edits, additional photos, RSVP exports for caterers, and any last-minute schedule changes. After the wedding, you can keep the site live as a digital wedding archive or migrate it to a permanent anniversary URL.",
        ],
      },
    ],
    faqs: [
      {
        q: "What's the difference between a builder tool and your service?",
        a: "Builder tools (like the well-known DIY platforms) give you a template and an editor — you do the work. Our service is fully done-for-you: we design, build, host and maintain the site. You only review and approve.",
      },
      {
        q: "Can I edit the wedding website myself after launch?",
        a: "Yes — every wedding website we build comes with a simple admin dashboard for couple-side edits (photos, schedule changes, RSVP exports). For major design changes, our team makes the edits for you.",
      },
      {
        q: "Do you provide a custom domain?",
        a: "Yes — we register a custom domain (yournames.com or yournames.wedding) and set up SSL security. The domain is yours to keep forever.",
      },
      {
        q: "How long does the builder service take?",
        a: "Standard build is 5-7 days from brief to live URL. Express builds (3 days) are available for urgent dates.",
      },
      {
        q: "Does the wedding website come with RSVP?",
        a: "Yes — every wedding website we build includes a full RSVP system with meal preference, plus-one tracking, and an admin dashboard showing live attendee counts.",
      },
      {
        q: "What happens after the wedding?",
        a: "You can keep the site live as a wedding archive (photos, video, thank-you notes for guests) or migrate it to a permanent anniversary URL. We offer post-wedding rebuilds at a small fee.",
      },
    ],
    relatedSlugs: [
      "wedding-website",
      "wedding-rsvp-website",
      "online-wedding-invitations",
      "digital-wedding-invitations",
      "destination-wedding-invitations",
    ],
  },
  {
    slug: "wedding-rsvp-website",
    title: "Wedding RSVP Website",
    eyebrow: "Track Every Guest",
    metaTitle:
      "Wedding RSVP Website | Online RSVP System | InvitationWala",
    metaDescription:
      "Beautiful RSVP-first wedding websites with meal tracking, plus-ones, function attendance and live admin dashboards. Built around your invite design.",
    keywords: [
      "wedding rsvp website",
      "online rsvp wedding",
      "rsvp wedding site",
      "wedding rsvp tracker",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "Tracking RSVPs across two hundred guests, four functions and three languages is the unsung nightmare of every wedding. A dedicated wedding RSVP website removes that friction entirely. At InvitationWala we build RSVP-first wedding websites that collect every detail you need — attendance, meal preference, plus-ones, function-by-function counts — and present it on a clean admin dashboard for the catering and seating teams.",
    sections: [
      {
        h2: "What our RSVP system collects",
        paragraphs: [
          "Every RSVP form is customised to your wedding's structure. For a four-function destination wedding we collect attendance per function (Mehndi, Sangeet, Wedding, Reception), meal preference (veg / non-veg / Jain / vegan / gluten-free), plus-one details, child meal needs, accommodation requirement, and arrival/departure flight numbers if it's a travel-included guest.",
        ],
        bullets: [
          "Per-function attendance tracking",
          "Meal preference: veg, non-veg, Jain, vegan, gluten-free",
          "Plus-one details with full name + meal pref",
          "Children: ages + child-meal needs",
          "Accommodation: required / self-arranged",
          "Travel: flight numbers for transfers",
          "Custom field — anything you'd like to ask",
        ],
      },
      {
        h2: "Live admin dashboard",
        paragraphs: [
          "Your wedding planner and catering team get a live admin dashboard showing every RSVP as it comes in. Filter by function, sort by meal preference, export to CSV for the caterer, and download a printable seating list. You can also send RSVP reminders to guests who haven't responded by a chosen cutoff date.",
        ],
      },
      {
        h2: "Guest-friendly experience",
        paragraphs: [
          "The guest side of every RSVP system is built for speed — three taps to confirm, one screen to choose meal, and an automatic save-the-date calendar link at the end. We support 10+ languages and dark/light mode, and the form works on every phone without an app download.",
        ],
      },
    ],
    faqs: [
      {
        q: "Why use an RSVP website instead of WhatsApp messages?",
        a: "An RSVP website centralises every response in one searchable dashboard — no scrolling through 200 WhatsApp threads to count vegetarians. It also lets you collect plus-ones, meal preferences and travel info in one structured form.",
      },
      {
        q: "Can the RSVP site track multiple functions?",
        a: "Yes — every function (Mehndi, Sangeet, Wedding, Reception, Cocktail) is tracked separately so the catering team knows exact counts per event.",
      },
      {
        q: "Can I export the guest list for the caterer?",
        a: "Yes — one-click CSV export of every response, filterable by function, meal type and plus-ones. Most caterers and seating planners accept this format directly.",
      },
      {
        q: "Can guests RSVP in regional languages?",
        a: "Yes — the entire RSVP form can be presented in Hindi, Urdu, Punjabi, Gujarati, Marathi, Tamil, Telugu, Bengali, Malayalam, Kannada or English. Toggle is automatic based on browser language.",
      },
      {
        q: "What happens after the RSVP cutoff date?",
        a: "We can automatically close new responses and send polite reminders to non-responders with a single click. Late responses are flagged separately so you can decide whether to accept them.",
      },
      {
        q: "Is the RSVP data secure?",
        a: "Yes — all RSVP data is encrypted, hosted on a global CDN with SSL, and accessible only to you and your wedding planner via a passworded admin link. Data is deleted 90 days after the wedding unless you request retention.",
      },
    ],
    relatedSlugs: [
      "wedding-website",
      "wedding-website-builder",
      "digital-wedding-invitations",
      "destination-wedding-invitations",
      "online-wedding-invitations",
    ],
  },
  {
    slug: "wedding-stationery",
    title: "Wedding Stationery",
    eyebrow: "Sister Brand · Stationery Wala",
    metaTitle:
      "Wedding Stationery Online | Premium Printed Cards | InvitationWala",
    metaDescription:
      "Premium wedding stationery — welcome signs, itinerary cards, menus, escort cards, luggage tags and more. Letterpressed, foiled and shipped across India & abroad.",
    keywords: [
      "wedding stationery",
      "premium wedding stationery",
      "wedding stationery online",
      "letterpressed wedding stationery",
    ],
    heroImage: HERO_WELCOME,
    intro:
      "Wedding stationery is the printed, tangible layer of your wedding's design language — the welcome signs guests photograph, the itinerary cards they carry through every function, the menus on every table, the favour tags they take home. At InvitationWala we design every stationery piece in-house and produce it through our sister brand Stationery Wala — letterpressed, foiled, laser-cut and shipped across India and internationally for destination & NRI weddings.",
    sections: [
      {
        h2: "Stationery pieces we design",
        paragraphs: [
          "Every piece of wedding stationery is designed to share the same visual DNA as your digital invitation — same typography, same palette, same motifs. The welcome sign greeting your guests, the itinerary card in their welcome hamper, the menu at their table, the favour tag on their take-home — every touchpoint rhymes.",
        ],
        bullets: [
          "Welcome signs (foamboard, acrylic, mirror)",
          "Itinerary & timeline cards",
          "Menus & place cards",
          "Escort cards & seating charts",
          "Favour tags & luggage tags",
          "Custom playing cards",
          "Wedding day newspaper",
          "Save-the-date magnets & postcards",
        ],
      },
      {
        h2: "Print finishes we offer",
        paragraphs: [
          "We work with the finest paper mills and print finishers in India to deliver stationery that looks and feels exceptional. Letterpress impressions on cotton paper, hot-foil stamping in gold/rose-gold/silver/copper, laser-cut intricate motifs, edge painting, deckle edges, blind embossing — every finish is selected to match the aesthetic of your wedding.",
        ],
        bullets: [
          "Letterpress on 600gsm cotton paper",
          "Hot foil stamping — gold, rose-gold, silver, copper, holographic",
          "Laser-cut intricate motifs",
          "Edge painting & deckle edges",
          "Blind embossing & debossing",
          "Premium card stocks — Mohawk, Crane, Plike",
        ],
      },
      {
        h2: "Shipping across India and internationally",
        paragraphs: [
          "Stationery Wala ships across India via Bluedart Express and internationally via DHL Express. Domestic delivery is 5-7 business days from production. International delivery is 7-10 business days. Destination wedding packages (welcome hampers, itinerary kits, favour boxes) ship in protective packaging and arrive ready-assembled at your venue.",
        ],
      },
    ],
    faqs: [
      {
        q: "What's included in a wedding stationery package?",
        a: "A standard stationery package includes welcome signs, itinerary cards, menus, escort cards, table numbers and favour tags. Premium packages add custom playing cards, wedding-day newspapers, luggage tags and personalised stationery boxes.",
      },
      {
        q: "Do you print and ship the stationery?",
        a: "Yes — our sister brand Stationery Wala handles printing and shipping across India and internationally. You don't need a separate vendor for production.",
      },
      {
        q: "How long does printed wedding stationery take?",
        a: "Production is 7-10 business days after design approval. Domestic shipping adds 5-7 days, international shipping adds 7-10 days. Plan to finalise design at least 4 weeks before the first function.",
      },
      {
        q: "Can the stationery match my digital invitation?",
        a: "Yes — that's the whole idea. Every printed piece shares the same typography, palette and motifs as your digital invite, giving guests one continuous brand experience.",
      },
      {
        q: "Do you ship to destination weddings abroad?",
        a: "Yes — Stationery Wala ships destination wedding stationery to Dubai, London, Singapore, Bali, Phuket, Tuscany, Lake Como, Mauritius and most popular destination wedding locations via DHL Express.",
      },
      {
        q: "What's the minimum order quantity?",
        a: "No strict minimum — most couples order between 50-500 of each piece. We can produce as few as 25 welcome signs or as many as 1000 menus.",
      },
    ],
    relatedSlugs: [
      "wedding-welcome-sign",
      "wedding-timeline-card",
      "wedding-itinerary-card",
      "wedding-accessories",
      "destination-wedding-invitations",
    ],
  },
  {
    slug: "save-the-date-cards",
    title: "Save The Date Cards",
    eyebrow: "Your First Reveal",
    metaTitle:
      "Save The Date Cards | Custom Save-The-Date Designs | InvitationWala",
    metaDescription:
      "Stunning custom save the date cards — animated reels, photo cards and print designs. Ready in 24 hours. Get yours on WhatsApp.",
    keywords: [
      "save the date cards",
      "save the date design",
      "save the date reel",
      "wedding save the date",
    ],
    heroImage: HERO_NISH,
    intro:
      "A save-the-date card is your wedding's first impression on the world — the moment your families and friends officially know it's happening. At InvitationWala we design save-the-date cards as cinematic 15-second reels, animated photo cards, and beautifully printed magnets — each version designed to feel like the opening frame of a film, not a logistics announcement.",
    sections: [
      {
        h2: "Save the date formats we design",
        paragraphs: [
          "Most couples send save-the-dates 3-6 months before the wedding. We design three primary formats based on how you want to share — a cinematic reel for Instagram and WhatsApp status, an animated photo card for WhatsApp broadcast, and an optional printed magnet or postcard mailed to grandparents and key family.",
        ],
        bullets: [
          "Cinematic save-the-date reel (15-30 sec, vertical 9:16)",
          "Animated photo card (MP4, 1:1 square, for broadcast)",
          "Printed magnets — refrigerator-ready (sister brand Stationery Wala)",
          "Printed postcards — premium paper, foil-stamped",
          "Calendar-link version — adds itself to guest's iPhone/Android calendar",
        ],
      },
      {
        h2: "What goes on a save-the-date",
        paragraphs: [
          "A save-the-date is intentionally minimal. Your names, the wedding date, the city, and one beautiful image or video moment. Full venue details, function schedule and dress code come later via the wedding invitation suite. The job of the save-the-date is simply to lock the date in your guests' calendars — and to start building anticipation.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should we send save-the-date cards?",
        a: "Send save-the-dates 4-6 months before the wedding (8-12 months for destination weddings where guests need to book flights and hotels). Send the full wedding invitation 6-8 weeks before the wedding.",
      },
      {
        q: "Is a save-the-date reel different from a wedding invite reel?",
        a: "Yes — a save-the-date reel is shorter (15-30 sec), minimal in detail, and built for shareability. The full wedding invite is 30-90 sec and includes every function, venue and dress code.",
      },
      {
        q: "Can we use our pre-wedding shoot photos?",
        a: "Absolutely — that's one of the most loved formats. Send us your pre-wedding shoot, we'll edit, colour-grade and animate it into a 15-second save-the-date reel with your wedding date overlay.",
      },
      {
        q: "Do you make printed save-the-date magnets?",
        a: "Yes — through our sister brand Stationery Wala. Magnets, postcards and luxury card formats, shipped across India and internationally.",
      },
      {
        q: "Can the save-the-date add itself to guest calendars?",
        a: "Yes — we include a 'Add to Calendar' link at the end of every save-the-date that adds the wedding to iPhone/Android calendars and Google Calendar in one tap.",
      },
      {
        q: "How fast can we get a save-the-date?",
        a: "Most save-the-dates are ready within 24 hours of brief. Express same-day delivery available for urgent timelines.",
      },
    ],
    relatedSlugs: [
      "digital-wedding-invitations",
      "online-wedding-invitations",
      "wedding-invitation-cards",
      "personalized-wedding-invitations",
      "wedding-website",
    ],
  },
  {
    slug: "wedding-welcome-sign",
    title: "Wedding Welcome Sign",
    eyebrow: "First Touchpoint",
    metaTitle:
      "Wedding Welcome Sign | Custom Welcome Signs | InvitationWala",
    metaDescription:
      "Premium wedding welcome signs — acrylic, mirror, foam, foiled paper. Custom designed to match your invitation. Ship across India and internationally.",
    keywords: [
      "wedding welcome sign",
      "welcome sign wedding",
      "custom wedding welcome sign",
      "wedding entrance sign",
    ],
    heroImage: HERO_WELCOME,
    intro:
      "Your wedding welcome sign is the first physical touchpoint of your wedding — the moment a guest steps into the venue and knows they're in the right place. At InvitationWala we design wedding welcome signs in every premium format — acrylic with hand-lettered gold foil, mirror with etched typography, foamboard with foil, paper with letterpress — each custom-designed to extend the design language of your invitation into the physical venue.",
    sections: [
      {
        h2: "Welcome sign materials we offer",
        paragraphs: [
          "Material choice changes the entire feel of your welcome sign. Acrylic is modern and clean; mirror is glamorous and reflective; foamboard is lightweight and travel-friendly; premium card is classic and tactile. We help you pick the right material based on your venue, weather, transportation logistics and overall aesthetic.",
        ],
        bullets: [
          "Acrylic with hand-applied gold/rose-gold foil",
          "Mirror with etched typography",
          "Foamboard with foil stamping",
          "Premium card with letterpress + foil",
          "Wooden hand-painted signs (artisan-crafted)",
          "Standing easel signs (free-standing, no wall mount needed)",
        ],
      },
      {
        h2: "Sign sizes and placements",
        paragraphs: [
          "We design welcome signs in three standard sizes — small (A2, for cocktail tables), medium (24×36 inch, for entrance walls) and large (36×48 inch, for venue gates). Custom sizes are available for large open-air venues, multi-panel installations and arch-style entrance signs.",
        ],
      },
      {
        h2: "Beyond the main welcome sign",
        paragraphs: [
          "Most weddings use one main welcome sign at the entrance — but a thoughtful welcome experience often includes function-specific signs (Mehndi welcome, Sangeet welcome, Reception welcome), directional signs (Cocktails this way, Dinner →), and bar/dessert station signage. We design every sign in the same design language for one seamless venue experience.",
        ],
      },
    ],
    faqs: [
      {
        q: "What size should my wedding welcome sign be?",
        a: "For a venue entrance — 24×36 inch (medium) is standard. For larger open-air venues with a tall entrance — 36×48 inch (large) reads better from distance. For cocktail-only welcome — A2 is sufficient.",
      },
      {
        q: "How long does a custom welcome sign take to produce?",
        a: "Production is 7-10 business days after design approval. Domestic shipping (India) adds 5-7 days. Plan to finalise design at least 3 weeks before the wedding.",
      },
      {
        q: "Can the welcome sign match my digital invitation?",
        a: "Yes — every welcome sign we design shares the same typography, palette and motifs as your invite suite, giving guests one continuous brand experience.",
      },
      {
        q: "Do you ship welcome signs internationally for destination weddings?",
        a: "Yes — we ship welcome signs via DHL Express to destination wedding locations including Dubai, Bali, Phuket, Tuscany, Lake Como, Mauritius and more.",
      },
      {
        q: "Can I have multiple welcome signs at the venue?",
        a: "Absolutely — most weddings use one large entrance sign + smaller function-specific signs at each event area + directional signage. We design every piece in the same visual language.",
      },
      {
        q: "What if I want a welcome sign in a regional language?",
        a: "We typeset welcome signs in Hindi, Urdu, Punjabi, Gujarati, Tamil, Marathi, Bengali, Malayalam, Kannada and Telugu with traditional script conventions.",
      },
    ],
    relatedSlugs: [
      "wedding-stationery",
      "wedding-timeline-card",
      "wedding-itinerary-card",
      "wedding-accessories",
      "destination-wedding-invitations",
    ],
  },
  {
    slug: "wedding-timeline-card",
    title: "Wedding Timeline Card",
    eyebrow: "Hourly Itinerary",
    metaTitle:
      "Wedding Timeline Card | Function Schedule Cards | InvitationWala",
    metaDescription:
      "Beautifully designed wedding timeline cards — hourly function schedules for guests. Digital + printable formats. Custom to match your invitation suite.",
    keywords: [
      "wedding timeline card",
      "wedding schedule card",
      "wedding day timeline",
      "wedding itinerary timeline",
    ],
    heroImage: HERO_NEWS,
    intro:
      "A wedding timeline card is the small but crucial piece of stationery that tells guests exactly when each function begins, where to be and what to expect. Especially for two-day, three-day and destination weddings — the timeline card prevents the 'Wait, what time is the Sangeet?' question being asked one hundred times. At InvitationWala we design wedding timeline cards as digital PDFs (sent on WhatsApp) and printed cards (placed in welcome hampers).",
    sections: [
      {
        h2: "What goes on a wedding timeline card",
        paragraphs: [
          "A timeline card lists every function across every day with start time, venue, dress code and any special instructions (transport pick-up, photographer time-slots, etc.). For destination weddings we add flight details, hotel check-in, welcome dinner timing, and the final brunch.",
        ],
        bullets: [
          "Hourly schedule across every wedding day",
          "Function venue + room name",
          "Dress code per function",
          "Transport pick-up & drop-off times",
          "Photography time-slots (for family groupings)",
          "Optional QR code linking to the wedding website",
        ],
      },
      {
        h2: "Digital + printed formats",
        paragraphs: [
          "Every wedding timeline card we design is delivered as a digital PDF (perfect for WhatsApp broadcast and the wedding website) and as a print-ready vector file (for couples who'd like physical cards in welcome hampers). Most couples use both — digital for the wider guest list, printed for VIP and out-of-town guests.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a wedding timeline card the same as an itinerary card?",
        a: "Closely related — a timeline card focuses on the hourly schedule (when things happen), an itinerary card focuses on the journey across days (what guests should plan for). Many couples combine both onto a single elegant card.",
      },
      {
        q: "How early should the timeline card go to guests?",
        a: "Send the digital timeline card with your wedding invitation suite (6-8 weeks before). Place the printed version in welcome hampers and hotel rooms on arrival day.",
      },
      {
        q: "Can the timeline card be in multiple languages?",
        a: "Yes — we design bilingual timeline cards (English + Hindi/Urdu/etc.) which work especially well for inter-community and NRI weddings.",
      },
      {
        q: "Should I include venue addresses on the timeline card?",
        a: "Include venue names and a QR code linking to the wedding website where guests get full addresses, maps and travel tips. This keeps the timeline card uncluttered.",
      },
      {
        q: "Can the timeline card have a QR code?",
        a: "Absolutely — we design custom branded QR codes that match your invite palette and link to your wedding website's schedule or RSVP page.",
      },
      {
        q: "What size should a printed timeline card be?",
        a: "DL (99×210mm) is the standard size — fits in welcome hampers, hotel room amenities and pocket. A5 works for more detailed schedules.",
      },
    ],
    relatedSlugs: [
      "wedding-itinerary-card",
      "wedding-stationery",
      "wedding-welcome-sign",
      "destination-wedding-invitations",
      "wedding-rsvp-website",
    ],
  },
  {
    slug: "wedding-itinerary-card",
    title: "Wedding Itinerary Card",
    eyebrow: "Multi-Day Journey",
    metaTitle:
      "Wedding Itinerary Card | Multi-Day Wedding Schedules | InvitationWala",
    metaDescription:
      "Premium wedding itinerary cards for destination and multi-day weddings. Hourly schedules, dress codes, travel tips. Digital + printed. 24-hour delivery.",
    keywords: [
      "wedding itinerary card",
      "wedding itinerary",
      "destination wedding itinerary",
      "wedding schedule card",
    ],
    heroImage: HERO_NEWS,
    intro:
      "A wedding itinerary card is the multi-day guidebook your guests carry through every function of your wedding. Especially for destination weddings (Udaipur, Goa, Jaipur, Bali, Phuket, Tuscany) and three-day Indian weddings (Mehndi → Haldi → Sangeet → Wedding → Reception), the itinerary card is what keeps your guest experience seamless. At InvitationWala we design every itinerary card as a printed insert for welcome hampers and a digital PDF on the wedding website.",
    sections: [
      {
        h2: "Itinerary card vs. timeline card",
        paragraphs: [
          "An itinerary card covers the multi-day journey — flight arrival timings, hotel check-in, welcome dinner, every function across every day, sightseeing recommendations, and the farewell brunch. A timeline card is narrower — just the hourly schedule of functions. Most couples use an itinerary card for destination weddings and a timeline card for in-city weddings.",
        ],
      },
      {
        h2: "What goes on a destination wedding itinerary",
        paragraphs: [
          "For destination weddings we typically structure the itinerary as Day 0 (Arrival), Day 1 (Welcome dinner + Mehndi), Day 2 (Haldi + Sangeet), Day 3 (Wedding day), Day 4 (Farewell brunch + departure). Each day lists timings, venues, dress codes, transport, and any local recommendations for free time.",
        ],
        bullets: [
          "Day-by-day breakdown across every wedding day",
          "Hotel check-in & check-out timings",
          "Function venues + dress codes",
          "Transport pickups (with vehicle types)",
          "Local sightseeing recommendations",
          "Welcome hamper contents preview",
          "Farewell brunch & departure logistics",
        ],
      },
    ],
    faqs: [
      {
        q: "Do we need an itinerary card for a single-day wedding?",
        a: "Usually not — a timeline card is sufficient for single-day weddings. Itinerary cards are most useful for multi-day weddings and destination weddings where guests travel and stay over.",
      },
      {
        q: "How early should we send the itinerary card?",
        a: "Send the digital itinerary 4-6 weeks before the wedding so guests can plan time off and book flights. Place the printed version in welcome hampers on arrival day.",
      },
      {
        q: "Can the itinerary card include flight & hotel info?",
        a: "Yes — for VIP guests and family we can include personalised flight arrival times and pre-booked hotel details. This works best as a printed insert in their welcome hamper.",
      },
      {
        q: "How long should the itinerary be?",
        a: "Most destination itineraries are 4-6 pages or fold-out card-style. Multi-day in-city itineraries are 2-3 pages. We design every layout based on your function count.",
      },
      {
        q: "Can the itinerary card be in welcome hampers?",
        a: "Yes — that's the most loved placement. The itinerary card goes inside the welcome hamper or on the hotel room dresser, ready for guests on arrival.",
      },
      {
        q: "Do you design the welcome hamper too?",
        a: "Yes — our sister brand Stationery Wala designs and produces welcome hampers (boxes, ribbons, custom tags, gift items) to match your wedding's design language.",
      },
    ],
    relatedSlugs: [
      "wedding-timeline-card",
      "wedding-welcome-sign",
      "wedding-stationery",
      "destination-wedding-invitations",
      "wedding-accessories",
    ],
  },
  {
    slug: "wedding-logo-design",
    title: "Wedding Logo Design",
    eyebrow: "Your Wedding's Identity",
    metaTitle:
      "Wedding Logo Design | Custom Wedding Logos | InvitationWala",
    metaDescription:
      "Bespoke wedding logo design — initials, monograms, illustrated marks. The signature that appears on every invitation, sign and favour. Custom to your story.",
    keywords: [
      "wedding logo design",
      "wedding logo",
      "wedding logo maker",
      "custom wedding logo",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "A wedding logo is the single visual signature that runs through every piece of your wedding's design — the invitation, the welcome sign, the menu, the favour tag, the wedding website, the photographer's slide deck. At InvitationWala we design custom wedding logos as monograms (interlocking initials), illustrated marks (deity, venue, motif), and typographic signatures (your names in a custom serif).",
    sections: [
      {
        h2: "Wedding logo styles we design",
        paragraphs: [
          "We design four primary wedding logo styles based on the couple's preference and wedding aesthetic. Monogram logos (interlocking initials with ornamental flourishes) are the most popular for classic weddings. Illustrated logos (deity, venue silhouette, animal totem) work beautifully for destination and themed weddings. Typographic logos (couple's names in elegant typography) feel modern and minimal. And combination logos blend two or more elements into one cohesive mark.",
        ],
        bullets: [
          "Monogram — interlocking initials with flourishes",
          "Illustrated — deity, venue silhouette, animal motif",
          "Typographic — couple's names in custom serif/script",
          "Combination — monogram + illustration + script in one mark",
          "Wax seal style — for old-world classic aesthetics",
        ],
      },
      {
        h2: "Where the wedding logo appears",
        paragraphs: [
          "Once your wedding logo is designed, it becomes the visual anchor across every wedding touchpoint. On the invitation cover. As the loading frame of the cinematic reel. On the welcome sign at every venue. On the menu cards. On the favour tags. As the watermark on photography preview albums. On the wedding website's favicon. On thank-you notes after the wedding. One mark, woven through the entire wedding journey.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does a wedding logo cost?",
        a: "Wedding logos are typically included free with our luxury and premium invitation suites. As a standalone service, custom wedding logo design is affordable — share your style preferences on WhatsApp for a quote.",
      },
      {
        q: "Can I trademark my wedding logo?",
        a: "Wedding logos are personal artwork, not commercial brands — so trademark filing is not typical. You receive the full vector file and copyright for personal use.",
      },
      {
        q: "How long does wedding logo design take?",
        a: "Wedding logos are typically delivered within 3-5 days. First concept routes (3-4 options) come within 48 hours, then refinement until approval.",
      },
      {
        q: "Can I use my wedding logo after the wedding?",
        a: "Absolutely — many couples use the wedding logo on anniversary cards, birth announcements, family holiday cards, and even etched on home accessories like decanters and trays.",
      },
      {
        q: "Will the logo work in print and on screen?",
        a: "Yes — every wedding logo is delivered as a vector file that scales infinitely without quality loss. It works on letterpress paper, embroidered table runners, etched mirror signs and animated reels.",
      },
      {
        q: "Can the logo be in Hindi or other scripts?",
        a: "Yes — we design monograms and typographic logos in Devanagari, Urdu, Gurmukhi, Tamil, Bengali and other scripts.",
      },
    ],
    relatedSlugs: [
      "wedding-monogram-design",
      "luxury-wedding-invitations",
      "premium-wedding-invitations",
      "personalized-wedding-invitations",
      "wedding-accessories",
    ],
  },
  {
    slug: "wedding-monogram-design",
    title: "Wedding Monogram Design",
    eyebrow: "Interlocking Initials",
    metaTitle:
      "Wedding Monogram Design | Custom Couple Monograms | InvitationWala",
    metaDescription:
      "Custom wedding monogram design — interlocking initials, ornamental flourishes, heirloom-grade. Appears across your invite, signage and stationery suite.",
    keywords: [
      "wedding monogram design",
      "wedding monogram",
      "couple monogram",
      "custom wedding monogram",
    ],
    heroImage: HERO_DEFAULT,
    intro:
      "A wedding monogram is the most heirloom-worthy of all wedding marks — two initials, interlocked with ornamental flourishes, becoming a single inseparable symbol. At InvitationWala we design wedding monograms with the patience of a jeweller — every flourish hand-drawn, every counter-form balanced, every serif weight chosen to make the two initials feel like one mark that has always existed.",
    sections: [
      {
        h2: "Monogram styles we design",
        paragraphs: [
          "We design wedding monograms across five major style traditions. Victorian monograms (heavy serifs, dense flourishes), Art Deco monograms (geometric, symmetrical, foil-friendly), Modern minimal monograms (single weight, geometric), Indian-traditional monograms (Devanagari or Gurmukhi initials with paisley flourishes), and Whimsical monograms (hand-drawn, slightly imperfect, romantic).",
        ],
        bullets: [
          "Victorian — heavy serifs, dense ornamental flourishes",
          "Art Deco — geometric symmetry, perfect for foil",
          "Modern minimal — single weight, clean lines",
          "Indian-traditional — Devanagari/Gurmukhi with paisley",
          "Whimsical — hand-drawn, romantic imperfection",
        ],
      },
      {
        h2: "Where wedding monograms work best",
        paragraphs: [
          "Wedding monograms are most striking when used sparingly — as the cover of the invitation, the loading frame of the reel, the embossed seal on the envelope, the foil stamp on the menu, the etched mark on welcome signs. We help you decide which 5-6 touchpoints will use the monogram so it stays special.",
        ],
      },
    ],
    faqs: [
      {
        q: "What's the difference between a monogram and a logo?",
        a: "A monogram is specifically built around interlocked initials — usually two letters becoming one symbol. A wedding logo is broader — it can be a monogram, an illustration, a wordmark or a combination.",
      },
      {
        q: "Can the monogram include both surnames?",
        a: "Yes — many monograms use 3 letters (groom's initial, surname initial, bride's initial) or 4 letters (both first names + both surnames). We balance the composition so no letter feels secondary.",
      },
      {
        q: "How long does a wedding monogram take to design?",
        a: "Typically 3-5 days. First concept routes (3-4 options) within 48 hours, then refinement until approval.",
      },
      {
        q: "Does the monogram come in different colours?",
        a: "Yes — we deliver every monogram as a vector file with multiple colour variations (gold, rose-gold, ivory, black, brand palette). It can also be reversed for use on dark backgrounds.",
      },
      {
        q: "Can the monogram be embroidered?",
        a: "Absolutely — many couples embroider their monogram on table runners, napkins, cushions and welcome hamper bags. We provide a vector file optimised for embroidery digitisation.",
      },
      {
        q: "Is the monogram included with my invitation suite?",
        a: "Yes — every luxury and premium wedding invitation suite includes a custom monogram or wedding logo at no extra cost.",
      },
    ],
    relatedSlugs: [
      "wedding-logo-design",
      "luxury-wedding-invitations",
      "premium-wedding-invitations",
      "wedding-accessories",
      "personalized-wedding-invitations",
    ],
  },
  {
    slug: "wedding-accessories",
    title: "Wedding Accessories",
    eyebrow: "The Finishing Touches",
    metaTitle:
      "Wedding Accessories | Custom Wedding Favours & Accessories | InvitationWala",
    metaDescription:
      "Custom wedding accessories — favour boxes, hand fans, return gifts, candle holders, photo booth signs and more. Match your wedding design language.",
    keywords: [
      "wedding accessories",
      "wedding favours",
      "wedding return gifts",
      "wedding decoration accessories",
    ],
    heroImage: HERO_TAGS,
    intro:
      "Wedding accessories are the small physical objects that finish your wedding's design language — favour boxes, hand fans, return gifts, candle holders, photo booth signs, table numbers, drink stirrers, place cards and more. At InvitationWala (via our sister brand Stationery Wala) we design every accessory to share the same typography, palette and motifs as your invitation suite — creating one truly cohesive wedding experience.",
    sections: [
      {
        h2: "Wedding accessories we design",
        paragraphs: [
          "We design over twenty categories of wedding accessories — every piece chosen because it shows up frequently in our most loved weddings. From the welcome hampers that greet guests on arrival, to the hand fans that double as ceremony programs, to the favour boxes guests carry home, every accessory is an opportunity to extend your wedding's story.",
        ],
        bullets: [
          "Favour boxes — laser-cut, foiled, custom-sized",
          "Hand fans — printed wedding programs (for warm-weather venues)",
          "Welcome hamper boxes — letterpressed lids, custom inserts",
          "Photo booth props & signs — themed to your wedding",
          "Drink stirrers & cocktail napkins — branded",
          "Candle holders & tea-light wraps",
          "Personalised playing cards",
          "Wedding day newspapers",
          "Petal toss cones",
          "Bouquet ribbons & boutonniere tags",
        ],
      },
      {
        h2: "How accessories tie your wedding together",
        paragraphs: [
          "Wedding accessories are the connective tissue that makes a wedding feel designed, not assembled. A guest who walks into a venue, sees the welcome sign, takes a printed hand-fan with the wedding monogram, sits at a table with branded place cards, drinks from cocktail stirrers with the couple's names, and leaves with a favour box embossed with the same monogram — that guest has been inside your wedding's world for hours, every detail rhyming.",
        ],
      },
    ],
    faqs: [
      {
        q: "Which wedding accessories are most popular?",
        a: "Favour boxes, welcome hampers and hand fans are the three most-loved. For destination weddings, luggage tags and welcome hampers; for in-city weddings, favour boxes and photo booth props; for summer weddings, branded hand fans that double as programs.",
      },
      {
        q: "Can the accessories match my wedding invitation?",
        a: "Yes — that's the whole idea. Every accessory shares the same typography, palette and motifs as your invitation suite, giving guests one continuous brand experience.",
      },
      {
        q: "How early should we order wedding accessories?",
        a: "Order 8-10 weeks before the wedding. Production is typically 3-4 weeks for custom accessories, plus shipping time.",
      },
      {
        q: "Do you handle production and shipping?",
        a: "Yes — Stationery Wala (our sister brand) handles production, quality check, packaging and shipping across India and internationally for destination weddings.",
      },
      {
        q: "What's the minimum order quantity?",
        a: "Most accessories have a minimum of 50-100 units. Larger custom items (welcome hampers, photo booth signs) can be produced in smaller quantities.",
      },
      {
        q: "Can the accessories be personalised per guest?",
        a: "Yes — favour boxes, place cards and luggage tags can be individually personalised with each guest's name. This adds production time (about a week extra) but creates a memorable touch.",
      },
    ],
    relatedSlugs: [
      "wedding-stationery",
      "wedding-welcome-sign",
      "wedding-itinerary-card",
      "destination-wedding-invitations",
      "wedding-monogram-design",
    ],
  },
  {
    slug: "personalized-wedding-invitations",
    title: "Personalized Wedding Invitations",
    eyebrow: "Made for Your Story",
    metaTitle:
      "Personalized Wedding Invitations | Custom Personalized Invites | InvitationWala",
    metaDescription:
      "Fully personalized wedding invitations — your photos, your story, your typography, your music. Custom designed in 24 hours. Delivered on WhatsApp.",
    keywords: [
      "personalized wedding invitations",
      "personalised wedding invitations",
      "custom personalised wedding invite",
      "personalized e-invite",
    ],
    heroImage: HERO_MUMBAI,
    intro:
      "Personalised wedding invitations are exactly that — personal. Not a template with your names dropped in, but a wedding invite built around your real photographs, your real city, your real love story, your real family heritage and your real wedding aesthetic. At InvitationWala every wedding invitation is custom personalised from a blank page — no templates, no presets, no AI shortcuts on the creative.",
    sections: [
      {
        h2: "What 'personalised' actually means",
        paragraphs: [
          "Most 'personalised' invite services online simply mean filling in your names on a template. We mean something much deeper. Personalised at InvitationWala means we use your pre-wedding shoot, your venue illustration, your favourite Bollywood track, your family deity, your city skyline, your monogram and your real palette — sampled from your actual outfits and venue mood-board.",
        ],
        bullets: [
          "Your real photographs and pre-wedding shoot",
          "Custom illustration of your venue / city / heritage",
          "Typography pair chosen for your aesthetic",
          "Music synced to your chosen track",
          "Palette sampled from your wedding outfits",
          "Family deity, mandap design, monogram woven in",
          "Function timeline matched exactly to your day",
        ],
      },
      {
        h2: "Personalisation across every function",
        paragraphs: [
          "Personalisation continues across every function card — your Mehndi card features the mehndi artist's signature pattern, your Sangeet card animates to your chosen song, your Haldi card uses warm yellow tones drawn from the haldi palette itself, and your wedding day film is timed to your venue's natural light at the ceremony hour.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you use templates for personalised invitations?",
        a: "Never. Every personalised invitation we design starts from a blank page with your specific story, photos, palette and references. We don't reuse illustrations or motion across couples.",
      },
      {
        q: "Can I use my pre-wedding shoot in the invitation?",
        a: "Absolutely — that's one of the most loved personalisation directions. Send us your pre-wedding shoot, we'll edit, colour-grade and animate it into the invite.",
      },
      {
        q: "How is personalised different from custom?",
        a: "They're synonymous in our world. We use 'personalised' because it emphasises the story behind the design — built around your real life, not just designed to your brief.",
      },
      {
        q: "Can the invite mention specific family members?",
        a: "Yes — we typeset both families' names (parents, grandparents, siblings) in correct hierarchy and traditional positioning. Bilingual versions are also available.",
      },
      {
        q: "Can the music be from a specific song?",
        a: "Yes — we sync your invite's motion to the song of your choice (Bollywood, original score, Sufi qawwali, classical, regional or international). Music licensing for personal-use weddings is included.",
      },
      {
        q: "How long does personalised invite design take?",
        a: "Typical personalised invitations are ready in 3-5 days. Full multi-function personalised suites take 5-7 days. Express same-day delivery is available for urgent timelines.",
      },
    ],
    relatedSlugs: [
      "customized-wedding-invitations",
      "luxury-wedding-invitations",
      "premium-wedding-invitations",
      "digital-wedding-invitations",
      "save-the-date-cards",
    ],
  },
  {
    slug: "customized-wedding-invitations",
    title: "Customized Wedding Invitations",
    eyebrow: "Designed Around You",
    metaTitle:
      "Customized Wedding Invitations | Fully Custom Designs | InvitationWala",
    metaDescription:
      "Customized wedding invitations — every detail tailored to your story, palette and functions. WhatsApp delivery in 24 hours. Custom quote within an hour.",
    keywords: [
      "customized wedding invitations",
      "customised wedding invitations",
      "custom wedding invite",
      "fully customised wedding invitations",
    ],
    heroImage: HERO_MUMBAI,
    intro:
      "Customized wedding invitations differ from generic invites the way couture differs from off-the-rack. Every element — the typography, the motion, the palette, the music, the illustration, the function timeline, the language — is shaped around your specific wedding. At InvitationWala we treat every invite as a fully custom commission, never a template-with-edits.",
    sections: [
      {
        h2: "Customisation across the entire invite",
        paragraphs: [
          "We customise nine layers of every invite. Typography (chosen for your aesthetic), palette (sampled from your venue and outfits), illustration (your city, deity, monogram or motif), motion (paced to your wedding's tone), music (synced to your chosen track), language (English + any regional language), function timeline (every event with venue & dress code), photography (your real moments), and family hierarchy (correctly typeset names of every parent and grandparent).",
        ],
        bullets: [
          "Typography — heritage faces chosen per aesthetic",
          "Palette — sampled from venue & outfits",
          "Illustration — city, deity, monogram or motif",
          "Motion — paced to your tone (cinematic or quick)",
          "Music — synced to your chosen track",
          "Language — English + regional script",
          "Family hierarchy — correct typeset of parents & grandparents",
          "Function timeline — every event mapped",
        ],
      },
      {
        h2: "Two routes to a customised invitation",
        paragraphs: [
          "We offer two customisation routes based on how much creative direction you want to give. The 'guided' route works best for couples who want us to lead — share your photos and references, we propose three creative directions, you choose one. The 'collaborative' route works best for design-savvy couples who want to direct specific palette, typography or motion choices themselves.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are customised invitations the same as personalised invitations?",
        a: "They overlap heavily — 'customised' emphasises the design tailoring, 'personalised' emphasises the personal story woven in. Every InvitationWala invite is both fully customised and personalised.",
      },
      {
        q: "Can I direct the design choices myself?",
        a: "Absolutely — we offer a 'collaborative' route where you specify palette, typography or motion choices yourself. Our team executes to your direction with our craft expertise applied.",
      },
      {
        q: "How customised can the invite be?",
        a: "Fully customised — every layer (typography, palette, illustration, motion, music, language, family hierarchy, function structure) is built around your wedding. No element is templated.",
      },
      {
        q: "Can I have different customisations across functions?",
        a: "Yes — each function card can have its own palette and motif while sharing the overall typography system. The Mehndi card can lean green-and-yellow, the Sangeet card can lean magenta-and-gold, and so on.",
      },
      {
        q: "Will I see the design before approving?",
        a: "Yes — every customised invite is built slide by slide with you on WhatsApp. You approve each frame before the next begins.",
      },
      {
        q: "Can the language script be customised too?",
        a: "Yes — we typeset in Hindi (Devanagari), Urdu (Nastaliq), Punjabi (Gurmukhi), Gujarati, Marathi, Tamil, Telugu, Bengali, Malayalam and Kannada with traditional script conventions.",
      },
    ],
    relatedSlugs: [
      "personalized-wedding-invitations",
      "luxury-wedding-invitations",
      "premium-wedding-invitations",
      "wedding-invitation-cards",
      "digital-wedding-invitations",
    ],
  },
  {
    slug: "royal-wedding-invitations",
    title: "Royal Wedding Invitations",
    eyebrow: "Heritage · Regal",
    metaTitle:
      "Royal Wedding Invitations | Regal & Heritage Designs | InvitationWala",
    metaDescription:
      "Royal wedding invitations inspired by heritage palaces, royal motifs and Mughal-era craftsmanship. Custom designed in gold-foiled regal aesthetic.",
    keywords: [
      "royal wedding invitations",
      "regal wedding invitations",
      "royal wedding card",
      "maharaja wedding invitation",
    ],
    heroImage: HERO_TEMPLE,
    intro:
      "A royal wedding invitation draws from the heritage that built India's most magnificent celebrations — Mughal-era miniature paintings, Rajasthani palace frescoes, South Indian temple stonework, and the gold-foiled ceremonial scripts of erstwhile princely states. At InvitationWala we design royal wedding invitations for couples whose weddings deserve the language of heritage — Udaipur palace weddings, Jaipur fort celebrations, Hyderabad nizami affairs, and destination weddings at Indian heritage hotels worldwide.",
    sections: [
      {
        h2: "Royal aesthetic traditions we draw from",
        paragraphs: [
          "We design royal wedding invitations across five major heritage traditions — Mughal (intricate floral arabesques, Persian script, blue and gold), Rajput (palace silhouettes, sword motifs, deep red and ivory), South Indian temple (Dravidian gopuram architecture, gold leaf, vermillion), Pahari/Kangra (miniature painting style, soft watercolour, romantic figuration), and Awadhi/Lucknowi (chikankari embroidery patterns, pastel palette, refined script).",
        ],
        bullets: [
          "Mughal — arabesques, Persian script, blue & gold",
          "Rajput — palace silhouettes, sword motifs, red & ivory",
          "South Indian temple — gopuram, gold leaf, vermillion",
          "Pahari miniature — watercolour figuration, soft palette",
          "Awadhi — chikankari patterns, refined Lucknowi script",
        ],
      },
      {
        h2: "Where royal invitations work best",
        paragraphs: [
          "Royal wedding invitations are most loved for weddings hosted at heritage venues — palaces, forts, havelis, heritage hotels and historic religious sites. They also work beautifully for couples drawing from family heritage (royal lineage, zamindari traditions, princely state connections) and for inter-cultural weddings where one family wants to honour heritage prominently in the design.",
        ],
      },
    ],
    faqs: [
      {
        q: "What makes an invitation 'royal' in design language?",
        a: "Royal invitations use heritage motifs (Mughal arabesques, Rajput palace silhouettes, temple architecture), gold-leaf or gold-foil treatments, ceremonial scripts (Devanagari, Nastaliq, traditional Sanskrit), and richly saturated palettes drawn from heritage textiles.",
      },
      {
        q: "Are royal wedding invitations only for royal families?",
        a: "Not at all — royal invitations are for any couple whose wedding aesthetic, venue or family heritage draws from royal traditions. We design royal invites for couples from every background.",
      },
      {
        q: "Can royal invitations be modern too?",
        a: "Yes — we often blend royal heritage motifs with modern minimalism — for example, a Mughal arabesque used as a delicate single border on a minimalist serif invite. The heritage element grounds the design without overwhelming it.",
      },
      {
        q: "What scripts work best for royal invitations?",
        a: "Sanskrit Devanagari, Persian Nastaliq, traditional Tamil, classical Bengali, and ornamental English serifs (Cormorant, Trajan, Optima). We typeset bilingual versions seamlessly.",
      },
      {
        q: "Can the royal invite be cinematic in motion?",
        a: "Absolutely — many royal invites we design feature opening reveal moments inspired by palace gates opening, drapes unfurling, or vermillion being applied in real-time. The motion is regal but never theatrical.",
      },
      {
        q: "Do you do printed royal invitations on premium paper?",
        a: "Yes — through Stationery Wala (our sister brand). Royal invites are printed on hand-made cotton paper with gold-foil stamping, deckle edges, and ceremonial envelope sealing.",
      },
    ],
    relatedSlugs: [
      "luxury-wedding-invitations",
      "premium-wedding-invitations",
      "destination-wedding-invitations",
      "wedding-monogram-design",
      "wedding-accessories",
    ],
  },
  {
    slug: "destination-wedding-invitations",
    title: "Destination Wedding Invitations",
    eyebrow: "Travel · Discovery",
    metaTitle:
      "Destination Wedding Invitations | Travel-Themed Invites | InvitationWala",
    metaDescription:
      "Destination wedding invitations with custom city illustrations, travel itineraries, hotel cards and RSVP microsites. Udaipur, Goa, Dubai, Bali, Tuscany & more.",
    keywords: [
      "destination wedding invitations",
      "destination wedding invite",
      "travel wedding invitation",
      "destination wedding card",
    ],
    heroImage: HERO_MUMBAI,
    intro:
      "Destination wedding invitations carry more responsibility than any other wedding invite — they invite guests to take time off work, book flights, pack a wardrobe and travel hundreds of kilometres to be part of your celebration. At InvitationWala we design destination wedding invitations that make the journey feel like the privilege it is — with custom city/venue illustrations, full multi-day itineraries, travel guides, and dedicated RSVP microsites that handle accommodation and transport.",
    sections: [
      {
        h2: "What goes into a destination wedding invite suite",
        paragraphs: [
          "A complete destination wedding suite is more than just an invitation. It includes the main wedding invite, a save-the-date 8-12 months before (so guests can plan), per-function cards, a multi-day itinerary, a travel guide for the destination, accommodation booking links, transport pick-up schedules, and a full RSVP microsite that tracks attendance, meal preferences, flight details and hotel needs.",
        ],
        bullets: [
          "Save-the-Date (sent 8-12 months ahead)",
          "Main wedding invitation suite (sent 4-6 months ahead)",
          "Custom illustration of destination/venue",
          "Multi-day itinerary card",
          "Travel guide & local recommendations",
          "Hotel & accommodation links",
          "Transport pick-up schedule",
          "RSVP microsite with flight & meal tracking",
        ],
      },
      {
        h2: "Popular destination wedding locations we design for",
        paragraphs: [
          "We have designed invitations for destination weddings in Udaipur, Jaipur, Jodhpur, Goa, Kerala backwaters, Hyderabad palaces, Mumbai luxury hotels, Bangalore vineyards — and internationally for Dubai, Abu Dhabi, Phuket, Bali, Lake Como, Tuscany, Mauritius, Maldives, Sri Lanka and Singapore. Each destination invite features a custom illustration of the venue's most recognisable architectural element.",
        ],
      },
      {
        h2: "Timing your destination wedding invitations",
        paragraphs: [
          "Destination weddings require longer guest lead-times than in-city weddings. Send save-the-dates 8-12 months before. Send the main invitation suite 4-6 months before. Send the welcome itinerary 4-6 weeks before. This timeline lets guests book flights at reasonable prices, request time off work, and arrange child care or pet sitting if needed.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should we send destination wedding invitations?",
        a: "Send save-the-dates 8-12 months before. Main invitation suite 4-6 months before. Detailed itinerary 4-6 weeks before. This gives guests enough lead time for flights, leave, and travel logistics.",
      },
      {
        q: "Should destination wedding invites be digital or printed?",
        a: "Most destination weddings use digital for the wider guest list (faster, cheaper, easier to update) and printed for VIP & family (premium experience, mailed with welcome hamper preview).",
      },
      {
        q: "Can the invite include hotel booking links?",
        a: "Yes — we add hotel booking links, group discount codes and accommodation FAQ to every destination wedding RSVP microsite. Guests can book in three taps.",
      },
      {
        q: "Do you design venue illustrations for destination invites?",
        a: "Absolutely — every destination invite typically features a custom illustration of the venue's most recognisable architectural element (palace gate, beach silhouette, mountain backdrop). It's the visual signature of the invite.",
      },
      {
        q: "Can we collect guest flight details?",
        a: "Yes — our RSVP microsite collects flight numbers, arrival/departure times, accommodation needs, and any special transport requirements. The data exports to CSV for your wedding planner.",
      },
      {
        q: "Do you ship printed destination wedding stationery internationally?",
        a: "Yes — Stationery Wala (our sister brand) ships welcome hampers, itinerary cards, place cards and favour boxes via DHL Express to every popular destination wedding location worldwide.",
      },
    ],
    relatedSlugs: [
      "wedding-itinerary-card",
      "wedding-website",
      "wedding-rsvp-website",
      "luxury-wedding-invitations",
      "wedding-welcome-sign",
    ],
  },
];

// Lookup helper
export const getServiceBySlug = (slug) =>
  SERVICES.find((s) => s.slug === slug);

export const getRelatedServices = (slug) => {
  const svc = getServiceBySlug(slug);
  if (!svc?.relatedSlugs) return [];
  return svc.relatedSlugs
    .map((s) => getServiceBySlug(s))
    .filter(Boolean);
};
